/**
 * K-Means clustering algorithm for Team Shape visualization
 * Custom implementation optimized for 2D coordinates with weighted points
 */

export interface ClusterPoint {
  x: number;
  y: number;
  weight?: number; // For weighted clustering (default: 1.0)
}

export interface Cluster {
  centroid: { x: number; y: number };
  points: ClusterPoint[];
  eventCount: number; // Number of events contributing to this cluster
  totalWeight: number; // Sum of weights for weighted averaging
}

export interface KMeansOptions {
  maxIterations?: number;
  tolerance?: number; // Convergence tolerance
  initialCentroids?: Array<{ x: number; y: number }>;
}

/**
 * Calculate Euclidean distance between two points
 */
function euclideanDistance(
  p1: { x: number; y: number },
  p2: { x: number; y: number }
): number {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Calculate weighted distance (distance * weight)
 */
function weightedDistance(
  point: ClusterPoint,
  centroid: { x: number; y: number }
): number {
  const distance = euclideanDistance(point, centroid);
  const weight = point.weight ?? 1.0;
  return distance * weight;
}

/**
 * Initialize centroids using deterministic k-means++ algorithm
 * Uses first point as initial centroid for consistency
 */
function initializeCentroids(
  points: ClusterPoint[],
  k: number
): Array<{ x: number; y: number }> {
  if (points.length === 0 || k === 0) {
    return [];
  }
  
  if (k >= points.length) {
    // If k >= number of points, use all points as centroids
    return points.map(p => ({ x: p.x, y: p.y }));
  }
  
  const centroids: Array<{ x: number; y: number }> = [];
  
  // First centroid: use first point (deterministic)
  centroids.push({ x: points[0].x, y: points[0].y });
  
  // Subsequent centroids: choose points farthest from existing centroids
  for (let i = 1; i < k; i++) {
    let maxDistance = 0;
    let farthestPoint: ClusterPoint | null = null;
    let farthestIndex = 0;
    
    for (let j = 0; j < points.length; j++) {
      const point = points[j];
      // Find minimum distance to any existing centroid
      let minDistance = Infinity;
      for (const centroid of centroids) {
        const dist = euclideanDistance(point, centroid);
        if (dist < minDistance) {
          minDistance = dist;
        }
      }
      
      // Choose point with maximum minimum distance
      // If tied, prefer earlier point in array for determinism
      if (minDistance > maxDistance || (minDistance === maxDistance && j < farthestIndex)) {
        maxDistance = minDistance;
        farthestPoint = point;
        farthestIndex = j;
      }
    }
    
    if (farthestPoint) {
      centroids.push({ x: farthestPoint.x, y: farthestPoint.y });
    } else {
      // Fallback: use next point in array (deterministic)
      const nextIndex = i < points.length ? i : i % points.length;
      centroids.push({ x: points[nextIndex].x, y: points[nextIndex].y });
    }
  }
  
  return centroids;
}

/**
 * K-Means clustering algorithm
 * 
 * @param points - Array of points to cluster
 * @param k - Number of clusters
 * @param options - Clustering options
 * @returns Array of clusters with centroids and assigned points
 */
export function kMeansClustering(
  points: ClusterPoint[],
  k: number,
  options: KMeansOptions = {}
): Cluster[] {
  const {
    maxIterations = 100,
    tolerance = 0.01,
    initialCentroids
  } = options;
  
  if (points.length === 0) {
    return [];
  }
  
  if (k <= 0) {
    return [];
  }
  
  if (k >= points.length) {
    // If k >= number of points, create one cluster per point
    return points.map((point, index) => ({
      centroid: { x: point.x, y: point.y },
      points: [point],
      eventCount: 1,
      totalWeight: point.weight ?? 1.0
    }));
  }
  
  // Initialize centroids
  let centroids: Array<{ x: number; y: number }> = initialCentroids || [];
  if (centroids.length === 0) {
    centroids = initializeCentroids(points, k);
  }
  
  let iterations = 0;
  let converged = false;
  
  while (iterations < maxIterations && !converged) {
    // Assign points to nearest centroid
    const clusters: Cluster[] = centroids.map(centroid => ({
      centroid: { ...centroid },
      points: [],
      eventCount: 0,
      totalWeight: 0
    }));
    
    for (const point of points) {
      let minDistance = Infinity;
      let nearestClusterIndex = 0;
      
      // Find nearest centroid
      for (let i = 0; i < centroids.length; i++) {
        const distance = weightedDistance(point, centroids[i]);
        if (distance < minDistance) {
          minDistance = distance;
          nearestClusterIndex = i;
        }
      }
      
      // Assign point to cluster
      clusters[nearestClusterIndex].points.push(point);
      clusters[nearestClusterIndex].eventCount++;
      clusters[nearestClusterIndex].totalWeight += point.weight ?? 1.0;
    }
    
    // Update centroids (weighted average)
    const newCentroids: Array<{ x: number; y: number }> = [];
    let maxCentroidShift = 0;
    
    for (const cluster of clusters) {
      if (cluster.points.length === 0) {
        // Empty cluster - keep old centroid
        const oldIndex = newCentroids.length;
        newCentroids.push({ ...centroids[oldIndex] });
        continue;
      }
      
      // Calculate weighted centroid
      let sumX = 0;
      let sumY = 0;
      let sumWeight = 0;
      
      for (const point of cluster.points) {
        const weight = point.weight ?? 1.0;
        sumX += point.x * weight;
        sumY += point.y * weight;
        sumWeight += weight;
      }
      
      const newCentroid = {
        x: sumX / sumWeight,
        y: sumY / sumWeight
      };
      
      newCentroids.push(newCentroid);
      
      // Track maximum centroid shift for convergence check
      const oldCentroid = centroids[newCentroids.length - 1];
      const shift = euclideanDistance(oldCentroid, newCentroid);
      if (shift > maxCentroidShift) {
        maxCentroidShift = shift;
      }
    }
    
    // Check convergence
    if (maxCentroidShift < tolerance) {
      converged = true;
    }
    
    centroids = newCentroids;
    iterations++;
  }
  
  // Final assignment (in case we exited due to max iterations)
  const finalClusters: Cluster[] = centroids.map(centroid => ({
    centroid: { ...centroid },
    points: [],
    eventCount: 0,
    totalWeight: 0
  }));
  
  for (const point of points) {
    let minDistance = Infinity;
    let nearestClusterIndex = 0;
    
    for (let i = 0; i < centroids.length; i++) {
      const distance = weightedDistance(point, centroids[i]);
      if (distance < minDistance) {
        minDistance = distance;
        nearestClusterIndex = i;
      }
    }
    
    finalClusters[nearestClusterIndex].points.push(point);
    finalClusters[nearestClusterIndex].eventCount++;
    finalClusters[nearestClusterIndex].totalWeight += point.weight ?? 1.0;
  }
  
  // Filter out empty clusters
  return finalClusters.filter(cluster => cluster.points.length > 0);
}

/**
 * Zone-constrained K-Means clustering
 * Clusters points within specified Y-coordinate zones
 * 
 * @param points - Array of points to cluster
 * @param zones - Array of zone constraints (minY, maxY, playerCount)
 * @param pitchHeight - Normalized pitch height (default: 100)
 * @returns Array of clusters from all zones
 */
export interface ZoneConstraint {
  minY: number;
  maxY: number;
  playerCount: number;
}

/**
 * Check if a point is in the goalkeeper area (6-yard box)
 * Based on pitch dimensions: 6-yard box is 55px deep from goal line
 * Goalkeeper is always on the defensive end (opposite to attacking direction)
 * Normalization doesn't change this - we use the team's actual attacking direction
 */
export function isInGoalkeeperArea(
  point: ClusterPoint,
  attackingDirection: 'left' | 'right',
  pitchWidth: number = 1050,
  pitchHeight: number = 680,
  normalizeToFirstHalf: boolean = false
): boolean {
  const sixYardDepth = 55; // 5.5m in pixels
  const sixYardWidth = 183; // 18.32m in pixels
  const sixYardY = (pitchHeight - sixYardWidth) / 2;
  const sixYardYEnd = sixYardY + sixYardWidth;
  
  // Goalkeeper is always on the defensive end (opposite to attacking direction)
  // Attacking right -> goalkeeper on left (defensive end)
  // Attacking left -> goalkeeper on right (defensive end)
  // Normalization doesn't affect this logic - we use the team's actual attacking direction
  
  if (attackingDirection === 'right') {
    // Attacking right means goalkeeper is on the left (defensive end, x < sixYardDepth)
    return point.x < sixYardDepth && 
           point.y >= sixYardY && 
           point.y <= sixYardYEnd;
  } else {
    // Attacking left means goalkeeper is on the right (defensive end, x > pitchWidth - sixYardDepth)
    return point.x > (pitchWidth - sixYardDepth) && 
           point.y >= sixYardY && 
           point.y <= sixYardYEnd;
  }
}

/**
 * Calculate goalkeeper position from goalkeeper-specific events
 * Returns the average position of goalkeeper events, constrained to 6-yard box
 * Goalkeeper is always on the defensive end (opposite to attacking direction)
 * Normalization doesn't change this - we use the team's actual attacking direction
 */
export function calculateGoalkeeperPosition(
  goalkeeperPoints: ClusterPoint[],
  attackingDirection: 'left' | 'right',
  pitchWidth: number = 1050,
  pitchHeight: number = 680,
  normalizeToFirstHalf: boolean = false
): Cluster | null {
  if (goalkeeperPoints.length === 0) {
    return null;
  }
  
  // Calculate weighted average
  let sumX = 0;
  let sumY = 0;
  let sumWeight = 0;
  
  for (const point of goalkeeperPoints) {
    const weight = point.weight ?? 1.0;
    sumX += point.x * weight;
    sumY += point.y * weight;
    sumWeight += weight;
  }
  
  const avgX = sumX / sumWeight;
  const avgY = sumY / sumWeight;
  
  // Constrain to 6-yard box
  const sixYardDepth = 55;
  const sixYardWidth = 183;
  const sixYardY = (pitchHeight - sixYardWidth) / 2;
  const sixYardYEnd = sixYardY + sixYardWidth;
  
  let constrainedX = avgX;
  let constrainedY = Math.max(sixYardY, Math.min(sixYardYEnd, avgY));
  
  // Goalkeeper is always on the defensive end (opposite to attacking direction)
  // Attacking right -> goalkeeper on left (defensive end)
  // Attacking left -> goalkeeper on right (defensive end)
  // Normalization doesn't affect this logic - we use the team's actual attacking direction
  
  if (attackingDirection === 'right') {
    // Goalkeeper on left (defensive end), constrain X to be within 6-yard box
    constrainedX = Math.max(0, Math.min(sixYardDepth, avgX));
  } else {
    // Goalkeeper on right (defensive end), constrain X to be within 6-yard box
    constrainedX = Math.max(pitchWidth - sixYardDepth, Math.min(pitchWidth, avgX));
  }
  
  return {
    centroid: { x: constrainedX, y: constrainedY },
    points: goalkeeperPoints,
    eventCount: goalkeeperPoints.length,
    totalWeight: sumWeight
  };
}

export function constrainedKMeans(
  points: ClusterPoint[],
  zones: ZoneConstraint[],
  pitchHeight: number = 100
): Cluster[] {
  const allClusters: Cluster[] = [];
  
  for (const zone of zones) {
    // Filter points by Y-coordinate (zone constraint)
    const zonePoints = points.filter(p => 
      p.y >= zone.minY && p.y <= zone.maxY
    );
    
    if (zonePoints.length === 0) {
      // No points in zone - skip
      continue;
    }
    
    // Run K-Means within zone using BOTH X and Y coordinates
    // This ensures players are distributed across X-axis, not all at same X
    const zoneClusters = kMeansClustering(zonePoints, zone.playerCount);
    allClusters.push(...zoneClusters);
  }
  
  return allClusters;
}

